import { useMemo, useState } from 'react';
import { Incident } from '../data/demoData';
import { apiFetch } from '../lib/apiClient';
import { useAuth } from '../context/AuthContext';
import { fmtTs } from '../lib/globalTz';

interface Props {
  incident: Incident;
  customerId: string;
}

interface CustomerUpdateResult {
  ok: true;
  incidentId: string;
  customerId: string;
  generatedAt: string;
  safeForCustomer: boolean;
  usedAi: boolean;
  model: string;
  subject: string;
  update: string;
  sections: { currentStatus: string; impact: string; actionsTaken: string[]; nextSteps: string[]; eta: string };
  redactionChecks: string[];
  jiraPayload: string;
  email: { subject: string; body: string };
}

function getIncidentId(incident: Incident): string {
  return String((incident as unknown as Record<string, unknown>).armIncidentName || incident.id || incident.incidentNumber || '').trim();
}

function toEml(subject: string, body: string): string {
  const now = new Date().toUTCString();
  return [
    'From: KloudSecure SOC <soc@kloudsecure.local>',
    'To: ',
    `Subject: ${subject.replace(/[\r\n]+/g, ' ')}`,
    `Date: ${now}`,
    'MIME-Version: 1.0',
    'Content-Type: text/plain; charset=UTF-8',
    '',
    body,
  ].join('\r\n');
}

function downloadText(filename: string, text: string, type = 'text/plain;charset=utf-8') {
  const blob = new Blob([text], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function CustomerUpdateGeneratorPanel({ incident, customerId }: Props) {
  const { canCapability } = useAuth();
  const canComment = canCapability('incidents', 'post_customer_update_comment');
  const canEmail = canCapability('incidents', 'export_customer_update_email');
  const canJira = canCapability('incidents', 'add_customer_update_to_jira');
  const [result, setResult] = useState<CustomerUpdateResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [analystContext, setAnalystContext] = useState('');
  const incidentId = useMemo(() => getIncidentId(incident), [incident]);

  async function generate() {
    setLoading(true); setError(null); setMessage(null);
    try {
      const data = await apiFetch<CustomerUpdateResult>(`/incidents/${encodeURIComponent(incidentId)}/customer-update/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId, incident, analystContext }),
      });
      setResult(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  async function auditAction(action: 'copy' | 'export_email' | 'jira' | 'comment') {
    if (!result) return null;
    return apiFetch<{ ok: boolean; message?: string; commentMethod?: string }>(`/incidents/${encodeURIComponent(incidentId)}/customer-update/action`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        customerId,
        incident,
        action,
        subject: result.subject,
        update: result.update,
        armIncidentName: (incident as unknown as Record<string, unknown>).armIncidentName,
        graphIncidentId: (incident as unknown as Record<string, unknown>).graphIncidentId,
      }),
    });
  }

  async function copyUpdate() {
    if (!result) return;
    setBusyAction('copy'); setError(null); setMessage(null);
    try {
      await navigator.clipboard.writeText(result.update);
      await auditAction('copy');
      setMessage('Customer update copied and audited.');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyAction(null);
    }
  }

  async function exportEmail() {
    if (!result) return;
    setBusyAction('email'); setError(null); setMessage(null);
    try {
      await auditAction('export_email');
      downloadText(`customer-update-${incident.incidentNumber || incidentId}.eml`, toEml(result.email.subject, result.email.body), 'message/rfc822');
      setMessage('Email draft exported and audited.');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyAction(null);
    }
  }

  async function prepareJira() {
    if (!result) return;
    setBusyAction('jira'); setError(null); setMessage(null);
    try {
      const resp = await auditAction('jira');
      await navigator.clipboard.writeText(result.jiraPayload);
      setMessage(resp?.message || 'Jira-safe payload copied and audited.');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyAction(null);
    }
  }

  async function addComment() {
    if (!result) return;
    setBusyAction('comment'); setError(null); setMessage(null);
    try {
      const resp = await auditAction('comment');
      setMessage(`Customer update added to incident comments${resp?.commentMethod ? ` via ${resp.commentMethod}` : ''}.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyAction(null);
    }
  }

  return (
    <section className="mt-3 rounded-xl border border-emerald-200 dark:border-emerald-900 bg-emerald-50/60 dark:bg-emerald-950/20 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-sm font-bold text-emerald-900 dark:text-emerald-100">One-Click Customer Update</p>
          <p className="text-xs text-emerald-800/80 dark:text-emerald-200/80">Customer-safe update generator: no raw KQL, no internal analyst notes, business-friendly status, impact, actions, next steps and ETA.</p>
        </div>
        <button onClick={generate} disabled={loading} className="rounded-lg bg-emerald-600 px-3 py-2 text-xs font-bold text-white hover:bg-emerald-700 disabled:opacity-60">
          {loading ? 'Generating…' : result ? 'Regenerate Update' : 'Generate Update'}
        </button>
      </div>

      <textarea
        value={analystContext}
        onChange={(e) => setAnalystContext(e.target.value)}
        placeholder="Optional analyst context to include, e.g. containment already performed, customer-specific communication preference. Internal notes will be sanitized from the customer output."
        className="mt-3 min-h-[70px] w-full rounded-lg border border-emerald-200 dark:border-emerald-900 bg-white/80 dark:bg-slate-950/40 p-2 text-xs outline-none focus:ring-2 focus:ring-emerald-500"
      />

      {error && <div className="mt-3 rounded-lg border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/30 p-2 text-xs text-red-700 dark:text-red-200">{error}</div>}
      {message && <div className="mt-3 rounded-lg border border-emerald-300 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/30 p-2 text-xs text-emerald-700 dark:text-emerald-200">{message}</div>}

      {result && (
        <div className="mt-4 space-y-3">
          <div className="grid gap-3 md:grid-cols-4">
            <div className="rounded-lg border border-border bg-card p-3"><p className="text-[11px] uppercase text-muted-foreground">Safe for customer</p><p className="text-sm font-bold">{result.safeForCustomer ? 'Yes' : 'Review'}</p></div>
            <div className="rounded-lg border border-border bg-card p-3"><p className="text-[11px] uppercase text-muted-foreground">AI path</p><p className="text-sm font-bold">{result.usedAi ? 'Azure OpenAI' : 'Fallback'}</p></div>
            <div className="rounded-lg border border-border bg-card p-3"><p className="text-[11px] uppercase text-muted-foreground">Generated</p><p className="text-sm font-bold">{fmtTs(result.generatedAt)}</p></div>
            <div className="rounded-lg border border-border bg-card p-3"><p className="text-[11px] uppercase text-muted-foreground">Redaction</p><p className="text-sm font-bold">{result.redactionChecks.length} checks</p></div>
          </div>
          <div className="rounded-lg border border-border bg-card p-3">
            <p className="text-xs font-bold text-foreground">{result.subject}</p>
            <pre className="mt-2 whitespace-pre-wrap rounded-lg bg-muted/50 p-3 text-xs text-foreground">{result.update}</pre>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={copyUpdate} disabled={busyAction !== null} className="rounded-lg border border-border bg-card px-3 py-2 text-xs font-semibold hover:bg-muted">{busyAction === 'copy' ? 'Copying…' : 'Copy update'}</button>
            {canComment && <button onClick={addComment} disabled={busyAction !== null} className="rounded-lg border border-border bg-card px-3 py-2 text-xs font-semibold hover:bg-muted">{busyAction === 'comment' ? 'Adding…' : 'Add to incident comment'}</button>}
            {canJira && <button onClick={prepareJira} disabled={busyAction !== null} className="rounded-lg border border-border bg-card px-3 py-2 text-xs font-semibold hover:bg-muted">{busyAction === 'jira' ? 'Preparing…' : 'Add to Jira / copy payload'}</button>}
            {canEmail && <button onClick={exportEmail} disabled={busyAction !== null} className="rounded-lg border border-border bg-card px-3 py-2 text-xs font-semibold hover:bg-muted">{busyAction === 'email' ? 'Exporting…' : 'Export as email'}</button>}
          </div>
        </div>
      )}
    </section>
  );
}
